import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import {
  addExternalSystem,
  removeExternalSystem,
} from "../../../../store/features/optionalSystemsSlice";
import { ExternalSystem } from "../../../../types/optionalSystems";
import {
  formatCurrency,
  formatWeight,
  formatPower,
} from "../../../../utils/formatting";
import { selectors } from "../../../../store/selectors";
import { SmallCraftAssignmentModal } from "./SmallCraftAssignmentModal";

interface ExternalSystemsProps {
  availableSystems: ExternalSystem[];
  shipTonnage: number;
  techLevel: number;
  isStreamlined?: boolean;
  tlOverrideActive?: boolean;
}

const ExternalSystems: React.FC<ExternalSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  isStreamlined = false,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const systems = useAppSelector((state) => state.optionalSystems.external);
  const [configSystem, setConfigSystem] = useState<ExternalSystem | null>(null);

  const handleAddSystem = (system: ExternalSystem) => {
    // For systems that need configuration, show config panel
    if (needsConfiguration(system)) {
      setConfigSystem(system);
    } else {
      // For simple systems, add directly
      const configuredSystem = configureSystem(system, shipTonnage);
      dispatch(addExternalSystem(configuredSystem));
    }
  };

  const handleRemoveSystem = (systemId: string) => {
    dispatch(removeExternalSystem(systemId));
  };

  const handleConfigureSystem = (system: ExternalSystem) => {
    const configuredSystem = configureSystem(system, shipTonnage);
    dispatch(addExternalSystem(configuredSystem));
    setConfigSystem(null);
  };

  const handleCancelConfig = () => {
    setConfigSystem(null);
  };

  const needsConfiguration = (system: ExternalSystem): boolean => {
    return ["dockingClamp", "forcedLinkage", "grapplingArm"].includes(
      system.subtype,
    );
  };

  const configureSystem = (
    system: ExternalSystem,
    shipTonnage: number,
  ): ExternalSystem => {
    // For systems that need configuration, return with basic properties
    // The actual calculation will be done when the system is configured
    return {
      ...system,
      cost: system.cost || 0,
      tonnage: system.tonnage || 0,
      power: system.power || 0,
    };
  };

  const renderConfigurationPanel = () => {
    if (!configSystem) return null;

    switch (configSystem.subtype) {
      case "dockingClamp":
        return (
          <DockingClampConfig
            system={configSystem}
            shipTonnage={shipTonnage}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );

      case "forcedLinkage":
        return (
          <ForcedLinkageConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );

      case "grapplingArm":
        return (
          <GrapplingArmConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">External Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Available External Systems
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableSystems
            .filter((system) => (tlOverrideActive ? true : techLevel >= system.techLevel))
            .map((system) => {
              const specs = configureSystem(system, shipTonnage);
              // Allow multiple installations for Docking Clamps, Forced Linkage Apparatus, and Grappling Arms
              const isInstalled =
                system.subtype === "dockingClamp" ||
                system.subtype === "forcedLinkage" ||
                system.subtype === "grapplingArm"
                  ? false // Always allow adding more of these systems
                  : systems.some((s) => s.id === system.id);

              // Count installed systems that allow multiples
              const installedDockingClamps =
                system.subtype === "dockingClamp"
                  ? systems.filter((s) => s.subtype === "dockingClamp").length
                  : 0;
              const installedForcedLinkages =
                system.subtype === "forcedLinkage"
                  ? systems.filter((s) => s.subtype === "forcedLinkage").length
                  : 0;
              const installedGrapplingArms =
                system.subtype === "grapplingArm"
                  ? systems.filter((s) => s.subtype === "grapplingArm").length
                  : 0;

              return (
                <div
                  key={system.id}
                  className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                >
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {system.name}
                      </h4>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                        TL{system.techLevel}
                      </span>
                    </div>
                    {system.subtype !== "dockingClamp" &&
                      (specs.cost > 0 ||
                        specs.tonnage > 0 ||
                        specs.power > 0) && (
                        <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                          {specs.cost > 0 && (
                            <div>
                              <b>Cost:</b> {formatCurrency(specs.cost)}
                            </div>
                          )}
                          {specs.tonnage > 0 && (
                            <div>
                              <b>Tonnage:</b> {formatWeight(specs.tonnage)}
                            </div>
                          )}
                          {specs.power > 0 && (
                            <div>
                              <b>Power:</b> {formatPower(specs.power)}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                  <div className="mt-2">
                    {system.subtype === "dockingClamp" ||
                    system.subtype === "forcedLinkage" ||
                    system.subtype === "grapplingArm" ? (
                      // Special handling for systems that allow multiple installations
                      <div>
                        <button
                          onClick={() => handleAddSystem(system)}
                          className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                        >
                          Add
                        </button>
                        {system.subtype === "dockingClamp" &&
                          installedDockingClamps > 0 && (
                            <div className="mt-2 text-xs text-gray-600 text-center">
                              {installedDockingClamps} installed
                            </div>
                          )}
                        {system.subtype === "forcedLinkage" &&
                          installedForcedLinkages > 0 && (
                            <div className="mt-2 text-xs text-gray-600 text-center">
                              {installedForcedLinkages} installed
                            </div>
                          )}
                        {system.subtype === "grapplingArm" &&
                          installedGrapplingArms > 0 && (
                            <div className="mt-2 text-xs text-gray-600 text-center">
                              {installedGrapplingArms} installed
                            </div>
                          )}
                      </div>
                    ) : (
                      // Standard handling for other systems
                      <>
                        {!isInstalled ? (
                          <button
                            onClick={() => handleAddSystem(system)}
                            className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                            disabled={isInstalled}
                          >
                            Add
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRemoveSystem(system.id)}
                            className="px-4 py-1 rounded text-white font-semibold bg-red-600 hover:bg-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Configuration Panel */}
      {renderConfigurationPanel()}

      {/* Installed External Systems section removed */}
    </div>
  );
};

// Configuration Components
interface ConfigProps {
  system: ExternalSystem;
  onConfigure: (system: ExternalSystem) => void;
  onCancel: () => void;
}

const DockingClampConfig: React.FC<ConfigProps & { shipTonnage: number }> = ({
  system,
  shipTonnage,
  onConfigure,
  onCancel,
}) => {
  const [clampType, setClampType] = useState(
    system.properties.clampType || "typeI",
  );
  const [showSmallCraftModal, setShowSmallCraftModal] = useState<boolean>(false);
  const [configuredSystem, setConfiguredSystem] = useState<any>(null);

  // Use selector for calculations
  const clampStats = useAppSelector((state) =>
    selectors.derived.selectDockingClampStats(state, clampType),
  );

  const clampTypes = [
    {
      value: "typeI",
      label: "Type I (1-30 tons)",
      maxTonnage: 30,
      cost: 50000,
      tonnage: 1,
    },
    {
      value: "typeII",
      label: "Type II (31-99 tons)",
      maxTonnage: 99,
      cost: 100000,
      tonnage: 2,
    },
    {
      value: "typeIII",
      label: "Type III (100-300 tons)",
      maxTonnage: 300,
      cost: 200000,
      tonnage: 3,
    },
    {
      value: "typeIV",
      label: "Type IV (301-2,000 tons)",
      maxTonnage: 2000,
      cost: 500000,
      tonnage: 5,
    },
    {
      value: "typeV",
      label: "Type V (2,001+ tons)",
      maxTonnage: 999999,
      cost: 1000000,
      tonnage: 10,
    },
  ];

  const selectedClamp = clampTypes.find((c) => c.value === clampType);

  const handleConfigure = () => {
    const configuredSys: ExternalSystem = {
      ...system,
      cost: clampStats.cost,
      tonnage: clampStats.tonnage,
      power: clampStats.power,
      properties: {
        ...system.properties,
        clampType,
        maxAttachedTonnage: clampStats.maxAttachedTonnage,
      },
    };
    setConfiguredSystem(configuredSys);
    setShowSmallCraftModal(true);
  };

  const handleSmallCraftModalClose = () => {
    setShowSmallCraftModal(false);
    setConfiguredSystem(null);
    onCancel();
  };

  const handleSmallCraftAssigned = () => {
    // Close modal after craft is assigned
    setShowSmallCraftModal(false);
    setConfiguredSystem(null);
    onCancel();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSkipSmallCraft = () => {
    // Add system without small craft assignment
    onConfigure(configuredSystem);
    setShowSmallCraftModal(false);
    setConfiguredSystem(null);
  };

  if (showSmallCraftModal && configuredSystem) {
    return (
      <SmallCraftAssignmentModal
        systemId={configuredSystem.id}
        systemName={`${system.name} (Type ${clampType.toUpperCase()})`}
        systemType="external"
        capacity={clampStats.maxAttachedTonnage === 999999 ? 'unlimited' : clampStats.maxAttachedTonnage}
        configuredSystem={configuredSystem}
        onClose={handleSmallCraftModalClose}
        onCraftAssigned={handleSmallCraftAssigned}
        onAddSystem={onConfigure}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Configure Docking Clamp</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clamp Type
            </label>
            <select
              value={clampType}
              onChange={(e) =>
                setClampType(
                  e.target.value as
                    | "typeI"
                    | "typeII"
                    | "typeIII"
                    | "typeIV"
                    | "typeV",
                )
              }
              className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            >
              {clampTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} - {type.cost}, {type.tonnage} tons
                </option>
              ))}
            </select>
          </div>
          {selectedClamp && (
            <div className="text-sm text-gray-600">
              <p>Cost: {formatCurrency(clampStats.cost)}</p>
              <p>Tonnage: {clampStats.tonnage} tons</p>
              <p>Max attached ship: {clampStats.maxAttachedTonnage} tons</p>
            </div>
          )}
          <div className="flex space-x-3">
            <button
              onClick={handleConfigure}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Add System & Assign Craft
            </button>
            <button
              onClick={onCancel}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-600">
          You can assign small craft later in the Cargo Configuration step if you skip now.
        </div>
      </div>
    </div>
  );
};

const ForcedLinkageConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [linkageType, setLinkageType] = useState(
    system.properties.linkageType || "basic",
  );

  // Use selector for calculations
  const linkageStats = useAppSelector((state) =>
    selectors.derived.selectForcedLinkageStats(state, linkageType),
  );

  const linkageTypes = [
    {
      value: "basic",
      label: "Basic (TL7)",
      pilotDM: -2,
      cost: 100000,
      tonnage: 2,
    },
    {
      value: "improved",
      label: "Improved (TL9)",
      pilotDM: -1,
      cost: 200000,
      tonnage: 2,
    },
    {
      value: "enhanced",
      label: "Enhanced (TL12)",
      pilotDM: 0,
      cost: 500000,
      tonnage: 2,
    },
    {
      value: "advanced",
      label: "Advanced (TL15)",
      pilotDM: 2,
      cost: 1000000,
      tonnage: 2,
    },
  ];

  const selectedLinkage = linkageTypes.find((l) => l.value === linkageType);

  const handleConfigure = () => {
    const configuredSystem: ExternalSystem = {
      ...system,
      cost: linkageStats.cost,
      tonnage: linkageStats.tonnage,
      power: linkageStats.power,
      properties: {
        ...system.properties,
        linkageType,
        pilotDM: linkageStats.pilotDM,
      },
    };
    onConfigure(configuredSystem);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">
          Configure Forced Linkage Apparatus
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Linkage Type
            </label>
            <select
              value={linkageType}
              onChange={(e) =>
                setLinkageType(
                  e.target.value as
                    | "basic"
                    | "improved"
                    | "enhanced"
                    | "advanced",
                )
              }
              className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            >
              {linkageTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} - {type.cost}, Pilot DM{" "}
                  {type.pilotDM >= 0 ? "+" : ""}
                  {type.pilotDM}
                </option>
              ))}
            </select>
          </div>
          {selectedLinkage && (
            <div className="text-sm text-gray-600">
              <p>Cost: {formatCurrency(linkageStats.cost)}</p>
              <p>
                Pilot DM: {linkageStats.pilotDM >= 0 ? "+" : ""}
                {linkageStats.pilotDM}
              </p>
              <p>Tonnage: {linkageStats.tonnage} tons</p>
              <p>Max target: 5,000 tons</p>
            </div>
          )}
          <div className="flex space-x-3">
            <button
              onClick={handleConfigure}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Add System
            </button>
            <button
              onClick={onCancel}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const GrapplingArmConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [armType, setArmType] = useState(
    system.properties.armType || "standard",
  );

  // Use selector for calculations
  const armStats = useAppSelector((state) =>
    selectors.derived.selectGrapplingArmStats(state, armType),
  );

  const armTypes = [
    {
      value: "standard",
      label: "Standard Grappling Arm",
      maxWeight: 2,
      cost: 50000,
      tonnage: 1,
    },
    {
      value: "heavy",
      label: "Heavy Grappling Arm",
      maxWeight: 10,
      cost: 200000,
      tonnage: 3,
    },
  ];

  const selectedArm = armTypes.find((a) => a.value === armType);

  const handleConfigure = () => {
    const configuredSystem: ExternalSystem = {
      ...system,
      cost: armStats.cost,
      tonnage: armStats.tonnage,
      power: armStats.power,
      properties: {
        ...system.properties,
        armType,
        maxWeight: armStats.maxWeight,
      },
    };
    onConfigure(configuredSystem);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Configure Grappling Arm</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Arm Type
            </label>
            <select
              value={armType}
              onChange={(e) =>
                setArmType(e.target.value as "standard" | "heavy")
              }
              className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            >
              {armTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} - {type.cost}, {type.tonnage} tons,{" "}
                  {type.maxWeight} tons max
                </option>
              ))}
            </select>
          </div>
          {selectedArm && (
            <div className="text-sm text-gray-600">
              <p>Cost: {formatCurrency(armStats.cost)}</p>
              <p>Tonnage: {armStats.tonnage} tons</p>
              <p>Max weight: {armStats.maxWeight} tons</p>
              <p>Reach: 250 metres</p>
            </div>
          )}
          <div className="flex space-x-3">
            <button
              onClick={handleConfigure}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Add System
            </button>
            <button
              onClick={onCancel}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExternalSystems;
